const text = value => typeof value === 'string' ? value.trim() : '';
const key = (...parts) => JSON.stringify(parts);
const blankHours = () => ({ bricklayer: 0, production: 0, all: 0 });

function number(value, label) {
  if ((typeof value !== 'number' && typeof value !== 'string') || value === '' ||
      (typeof value === 'string' && !value.trim()) || !Number.isFinite(Number(value)) || Number(value) < 0) {
    throw new Error(`${label} must be a non-negative number.`);
  }
  return Number(value);
}

export function productionUnitDetails(entry) {
  const recordedUnit = text(entry?.unit);
  const currentUnit = text(entry?.cost_code?.unit);
  const unitMismatch = Boolean(recordedUnit && currentUnit && recordedUnit !== currentUnit);
  return {
    recordedUnit,
    currentUnit,
    unitLabel: recordedUnit || 'Unit not recorded',
    unitMismatch,
    warning: !recordedUnit ? 'Recorded unit is missing; no unit inferred.' : unitMismatch
      ? `Recorded in ${recordedUnit}; current code uses ${currentUnit}. No conversion applied.` : null,
  };
}

// Accept the three fully fetched arrays from getProductionRates. No I/O and no
// mutation: quantity grouping must happen before a labor bucket is attached.
export function aggregateProductionRates({ production = [], timeEntries = [], trades = [] } = {}) {
  if (![production, timeEntries, trades].every(Array.isArray)) throw new Error('Production inputs must be arrays.');
  const tradeById = new Map(trades.map(trade => [trade.id, trade]));
  const countsAsProduction = employee => Boolean(employee &&
    (employee.production_override ?? tradeById.get(employee.trade_id)?.production_default));
  const isBricklayer = employee => tradeById.get(employee?.trade_id)?.name === 'Bricklayer';
  const bump = (bucket, employee, hours) => {
    bucket.all += hours;
    if (countsAsProduction(employee)) bucket.production += hours;
    if (isBricklayer(employee)) bucket.bricklayer += hours;
  };
  const byDay = new Map();
  const byCode = new Map();
  for (const entry of timeEntries) {
    if (!entry.job_id || ['draft', 'removed'].includes(text(entry.status).toLowerCase())) continue;
    const dayKey = key(entry.job_id, entry.work_date);
    if (!byDay.has(dayKey)) byDay.set(dayKey, { ...blankHours(), hasAllocations: false, openShift: false });
    const day = byDay.get(dayKey);
    if (entry.total_hours == null) {
      if (countsAsProduction(entry.employee) || isBricklayer(entry.employee)) day.openShift = true;
    } else {
      bump(day, entry.employee, number(entry.total_hours, 'Paid hours'));
    }
    for (const allocation of entry.allocations || []) {
      if (!allocation.cost_code_id) continue;
      day.hasAllocations = true;
      const codeKey = key(entry.job_id, entry.work_date, allocation.cost_code_id);
      if (!byCode.has(codeKey)) byCode.set(codeKey, blankHours());
      bump(byCode.get(codeKey), entry.employee, number(allocation.hours, 'Allocated hours'));
    }
  }

  const groups = new Map();
  const unitsPerCodeDay = new Map();
  const codesPerDay = new Map();
  for (const entry of production) {
    if (!entry.job_id || !entry.work_date || !entry.cost_code_id) throw new Error('Production entry is missing its job, day, or cost code.');
    const unit = productionUnitDetails(entry);
    const dayKey = key(entry.job_id, entry.work_date);
    const codeKey = key(entry.job_id, entry.work_date, entry.cost_code_id);
    const groupKey = key(entry.job_id, entry.work_date, entry.cost_code_id, unit.recordedUnit);
    if (!codesPerDay.has(dayKey)) codesPerDay.set(dayKey, new Set());
    codesPerDay.get(dayKey).add(entry.cost_code_id);
    if (!unitsPerCodeDay.has(codeKey)) unitsPerCodeDay.set(codeKey, new Set());
    unitsPerCodeDay.get(codeKey).add(unit.recordedUnit);
    if (!groups.has(groupKey)) groups.set(groupKey, {
      id: groupKey, date: entry.work_date, jobId: entry.job_id,
      costCodeId: entry.cost_code_id,
      jobName: entry.job?.name || 'Unknown Job', jobNumber: entry.job?.job_number || '',
      code: entry.cost_code?.code || '', codeName: entry.cost_code?.name || 'Unknown Code',
      unit: unit.unitLabel, recordedUnit: unit.recordedUnit, unitMismatch: false,
      quantity: 0, entryIds: [], notes: [], warnings: new Set(),
    });
    const group = groups.get(groupKey);
    group.quantity += number(entry.quantity, 'Quantity');
    group.entryIds.push(entry.id);
    if (text(entry.notes)) group.notes.push(text(entry.notes));
    group.unitMismatch ||= unit.unitMismatch;
    if (unit.warning) group.warnings.add(unit.warning);
  }

  const rows = [...groups.values()].map(group => {
    const dayKey = key(group.jobId, group.date);
    const codeKey = key(group.jobId, group.date, group.costCodeId);
    const day = byDay.get(dayKey);
    const exact = byCode.get(codeKey);
    const mixedUnits = unitsPerCodeDay.get(codeKey).size > 1;
    let source = null;
    let basis = 'none';
    let reason = null;
    if (!group.recordedUnit) {
      reason = 'recorded unit is missing; rate unavailable';
    } else if (mixedUnits) {
      // Allocations have no quantity-unit attribution. Reusing or splitting
      // this bucket between incompatible units would invent a denominator.
      reason = 'multiple recorded units on this code/day; labor cannot be assigned by unit';
    } else if (day?.openShift) {
      reason = 'someone is still clocked in - day not final';
    } else if (exact) {
      source = exact;
      basis = 'allocated';
    } else if (!day || !day.all) {
      reason = 'nobody clocked in on this job that day';
    } else if (day.hasAllocations) {
      reason = "that day's hours are allocated to other cost codes";
    } else {
      source = day;
      basis = 'whole-day';
    }
    const bricklayerHours = source?.bricklayer || 0;
    const productionHours = source?.production || 0;
    if (source && !bricklayerHours && !productionHours) reason = 'crew was there, but no production trades';
    const sharedWith = basis === 'whole-day' ? codesPerDay.get(dayKey).size : 1;
    const basisLabel = basis === 'whole-day'
      ? `Whole-day labor fallback (approximate${sharedWith > 1 ? `; shared across ${sharedWith} cost codes` : ''})`
      : basis === 'allocated' ? 'Cost-code allocated labor' : 'No attributable labor';
    return {
      ...group, notes: group.notes.join('\n'), warnings: [...group.warnings],
      entryCount: group.entryIds.length, mixedUnits,
      bricklayerHours, productionHours, allHours: day?.all || 0,
      basis, basisLabel, reason, sharedWith, approximate: basis === 'whole-day',
      ratePerBricklayerHour: bricklayerHours > 0 ? group.quantity / bricklayerHours : null,
      ratePerProductionHour: productionHours > 0 ? group.quantity / productionHours : null,
    };
  });

  const rollup = new Map();
  for (const row of rows) {
    const rollupKey = key(row.costCodeId, row.recordedUnit);
    if (!rollup.has(rollupKey)) rollup.set(rollupKey, {
      costCodeId: row.costCodeId, code: row.code, codeName: row.codeName, unit: row.unit,
      quantity: 0, bricklayerHours: 0, productionHours: 0,
      dayKeys: new Set(), daysWithHoursKeys: new Set(), approximate: false,
      unitMismatch: false, warnings: new Set(), incompleteBricklayer: false, incompleteProduction: false,
    });
    const total = rollup.get(rollupKey);
    total.quantity += row.quantity;
    total.bricklayerHours += row.bricklayerHours;
    total.productionHours += row.productionHours;
    const dayKey = key(row.jobId, row.date);
    total.dayKeys.add(dayKey);
    if (row.bricklayerHours > 0 || row.productionHours > 0) total.daysWithHoursKeys.add(dayKey);
    total.approximate ||= row.approximate;
    total.unitMismatch ||= row.unitMismatch;
    for (const warning of row.warnings) total.warnings.add(warning);
    if (row.reason) total.warnings.add(row.reason);
    total.incompleteBricklayer ||= row.bricklayerHours <= 0;
    total.incompleteProduction ||= row.productionHours <= 0;
  }
  const byCostCode = [...rollup.values()].map(total => {
    const { dayKeys, daysWithHoursKeys, incompleteBricklayer, incompleteProduction, warnings, ...values } = total;
    return {
      ...values, days: dayKeys.size, daysWithHours: daysWithHoursKeys.size,
      warnings: [...warnings],
      basisLabel: total.approximate ? 'Includes whole-day labor fallback (approximate)'
        : incompleteBricklayer && incompleteProduction ? 'Incomplete labor coverage; rate unavailable' : 'Cost-code allocated labor only',
      ratePerBricklayerHour: !incompleteBricklayer && total.bricklayerHours > 0 ? total.quantity / total.bricklayerHours : null,
      ratePerProductionHour: !incompleteProduction && total.productionHours > 0 ? total.quantity / total.productionHours : null,
    };
  }).sort((a, b) => b.quantity - a.quantity);
  return { rows, byCostCode, error: null };
}
